import {useCallback, useEffect, useLayoutEffect, useMemo, useRef} from 'react'
import {ActivityIndicator, StyleSheet} from 'react-native'
import {withSpring} from 'react-native-reanimated'
import {useFocusEffect} from '@react-navigation/native'

import {PROD_DEFAULT_FEED} from '#/lib/constants'
import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'
import {useOTAUpdates} from '#/lib/hooks/useOTAUpdates'
import {useSetTitle} from '#/lib/hooks/useSetTitle'
import {useRequestNotificationsPermission} from '#/lib/notifications/notifications'
import {
  type HomeTabNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {emitSoftReset} from '#/state/events'
import {
  homeEmptyState,
  homeFeedIndex,
  homeFeedPages,
} from '#/state/feed-ranking'
import {
  type SavedFeedSourceInfo,
  SYSTEM_FOR_YOU_FEED_INFO,
  usePinnedFeedsInfos,
} from '#/state/queries/feed'
import {type FeedDescriptor, type FeedParams} from '#/state/queries/post-feed'
import {usePreferencesQuery} from '#/state/queries/preferences'
import {type UsePreferencesQueryResponse} from '#/state/queries/preferences/types'
import {useSession} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {useSelectedFeed, useSetSelectedFeed} from '#/state/shell/selected-feed'
import {FeedPage} from '#/view/com/feeds/FeedPage'
import {HomeHeader} from '#/view/com/home/HomeHeader'
import {
  Pager,
  type PagerRef,
  type RenderTabBarFnProps,
} from '#/view/com/pager/Pager'
import {CustomFeedEmptyState} from '#/view/com/posts/CustomFeedEmptyState'
import {FollowingEmptyState} from '#/view/com/posts/FollowingEmptyState'
import {FollowingEndOfFeed} from '#/view/com/posts/FollowingEndOfFeed'
import {
  HomeHeaderModeProvider,
  useHomeHeaderMode,
} from '#/view/com/util/MainScrollProvider'
import {ForYouEmptyState} from '#/screens/Home/ForYouEmptyState'
import {NoFeedsPinned} from '#/screens/Home/NoFeedsPinned'
import {FlashBookShelf} from '#/components/flashbooks/FlashBookShelf'
import * as Layout from '#/components/Layout'
import {useAnalytics} from '#/analytics'
import {IS_LIQUID_GLASS, IS_WEB} from '#/env'
import {useDemoMode} from '#/storage/hooks/demo-mode'

type Props = NativeStackScreenProps<HomeTabNavigatorParams, 'Home' | 'Start'>
export function HomeScreen(props: Props) {
  const {setShowLoggedOut} = useLoggedOutViewControls()
  const {data: preferences} = usePreferencesQuery()
  const {currentAccount} = useSession()
  const {data: pinnedFeedInfos, isLoading: isPinnedFeedsLoading} =
    usePinnedFeedsInfos()

  useEffect(() => {
    if (IS_WEB && !currentAccount) {
      const getParams = new URLSearchParams(window.location.search)
      const splash = getParams.get('splash')
      if (splash === 'true') {
        setShowLoggedOut(true)
        return
      }
    }

    const params = props.route.params
    if (
      currentAccount &&
      props.route.name === 'Start' &&
      params?.name &&
      params?.rkey
    ) {
      props.navigation.navigate('StarterPack', {
        rkey: params.rkey,
        name: params.name,
      })
    }
  }, [
    currentAccount,
    props.navigation,
    props.route.name,
    props.route.params,
    setShowLoggedOut,
  ])

  if (preferences && pinnedFeedInfos && !isPinnedFeedsLoading) {
    return (
      <Layout.Screen testID="HomeScreen" noInsetTop={IS_LIQUID_GLASS}>
        <HomeHeaderModeProvider>
          <HomeScreenReady
            {...props}
            preferences={preferences}
            pinnedFeedInfos={pinnedFeedInfos}
          />
        </HomeHeaderModeProvider>
      </Layout.Screen>
    )
  } else {
    return (
      <Layout.Screen>
        <Layout.Center style={styles.loading}>
          <ActivityIndicator size="large" />
        </Layout.Center>
      </Layout.Screen>
    )
  }
}

function HomeScreenReady({
  preferences,
  pinnedFeedInfos,
}: Props & {
  preferences: UsePreferencesQueryResponse
  pinnedFeedInfos: SavedFeedSourceInfo[]
}) {
  const ax = useAnalytics()
  const selectedFeed = useSelectedFeed()
  const setSelectedFeed = useSetSelectedFeed()

  /*
   * The pages Home draws, in order.
   *
   * The reader's pinned feeds, with the system For-you page in front of them when
   * they have not pinned that feed themselves. The tab bar, the pager and the
   * title all read this one list, so the three cannot disagree — and it is the
   * only place the default is decided, which is why the choice is a function with
   * tests rather than a `??` in the middle of this component.
   */
  const homeFeedInfos = useMemo(
    () => homeFeedPages(pinnedFeedInfos, SYSTEM_FOR_YOU_FEED_INFO),
    [pinnedFeedInfos],
  )
  const allFeeds = useMemo(
    () => homeFeedInfos.map(f => f.feedDescriptor),
    [homeFeedInfos],
  )
  /*
   * Which page opens: the reader's stored choice if they have one, and otherwise
   * the ranked feed — not the first pinned tab, which is what a reader used to
   * get. Nothing is written here; the pager reports a page change and that report
   * is what gets saved.
   */
  const selectedIndex = Math.max(0, homeFeedIndex(homeFeedInfos, selectedFeed))
  const maybeSelectedFeed: FeedDescriptor | undefined = allFeeds[selectedIndex]
  const requestNotificationsPermission = useRequestNotificationsPermission()

  useSetTitle(homeFeedInfos[selectedIndex]?.displayName)
  useOTAUpdates()

  useEffect(() => {
    requestNotificationsPermission('Home')
  }, [requestNotificationsPermission])

  const pagerRef = useRef<PagerRef>(null)
  const lastPagerReportedIndexRef = useRef(selectedIndex)
  useLayoutEffect(() => {
    // Since the pager is not a controlled component, adjust it imperatively
    // if the selected index gets out of sync with what it last reported.
    // This is supposed to only happen on the web when you use the right nav.
    if (selectedIndex !== lastPagerReportedIndexRef.current) {
      lastPagerReportedIndexRef.current = selectedIndex
      pagerRef.current?.setPage(selectedIndex)
    }
  }, [selectedIndex])

  const {hasSession} = useSession()
  const headerMode = useHomeHeaderMode()
  const showHeader = useCallback(() => {
    'worklet'
    headerMode.set(() => withSpring(0, {overshootClamping: true}))
  }, [headerMode])

  useFocusEffect(
    useCallback(() => {
      return () => showHeader()
    }, [showHeader]),
  )

  useFocusEffect(
    useNonReactiveCallback(() => {
      if (maybeSelectedFeed) {
        ax.metric('home:feedDisplayed', {
          index: selectedIndex,
          feedType: maybeSelectedFeed.split('|')[0],
          feedUrl: maybeSelectedFeed,
          reason: 'focus',
        })
      }
    }),
  )

  const onPageSelected = useCallback(
    (index: number) => {
      showHeader()
      const maybeFeed = allFeeds[index]

      // Mutate the ref before setting state to avoid the imperative syncing effect
      // above from starting a loop on Android when swiping back and forth.
      lastPagerReportedIndexRef.current = index
      setSelectedFeed(maybeFeed)

      if (maybeFeed) {
        ax.metric('home:feedDisplayed', {
          index,
          feedType: maybeFeed.split('|')[0],
          feedUrl: maybeFeed,
        })
      }
    },
    [ax, setSelectedFeed, showHeader, allFeeds],
  )

  const onPressSelected = useCallback(() => {
    emitSoftReset()
  }, [])

  const onPageScrollStateChanged = useCallback(
    (state: 'idle' | 'dragging' | 'settling') => {
      'worklet'
      if (state === 'dragging') {
        showHeader()
      }
    },
    [showHeader],
  )

  const [demoMode] = useDemoMode()

  const renderTabBar = useCallback(
    (props: RenderTabBarFnProps) => {
      if (demoMode) {
        return (
          <HomeHeader
            key="FEEDS_TAB_BAR"
            {...props}
            testID="homeScreenFeedTabs"
            onPressSelected={onPressSelected}
            // @ts-ignore
            feeds={[{displayName: 'Following'}, {displayName: 'Discover'}]}
          />
        )
      }
      return (
        <HomeHeader
          key="FEEDS_TAB_BAR"
          {...props}
          testID="homeScreenFeedTabs"
          onPressSelected={onPressSelected}
          feeds={homeFeedInfos}
          pinnedFeeds={pinnedFeedInfos}
        />
      )
    },
    [onPressSelected, homeFeedInfos, pinnedFeedInfos, demoMode],
  )

  const renderFollowingEmptyState = useCallback(() => {
    return <FollowingEmptyState />
  }, [])

  const renderCustomFeedEmptyState = useCallback(() => {
    return <CustomFeedEmptyState />
  }, [])

  /*
   * The prompt for a reader who has pinned nothing.
   *
   * It used to be the whole screen, which meant a reader with no pinned feeds had
   * no feed at all. It is now the empty state of the page they land on instead:
   * the ranked feed fills the screen whenever it has something to show, and the
   * way to pin things is offered where it is actually needed.
   */
  const renderNoFeedsPinned = useCallback(() => {
    return <NoFeedsPinned preferences={preferences} />
  }, [preferences])

  /*
   * The ranked feed with nothing to show.
   *
   * Not the custom-feed empty state: that one asks the reader to follow people,
   * which is advice for a different feed and cannot fix a ranked feed that has
   * nothing eligible. See `homeEmptyState` for why the three blank pages are
   * told apart at all.
   */
  const renderForYouEmptyState = useCallback(() => {
    return <ForYouEmptyState />
  }, [])

  /*
   * The books the learner is part-way through, above the feed they land on.
   *
   * Only the front door gets it — the first page, which is the ranked feed when
   * the reader has not pinned it, or the logged-out feed, which is the same
   * screen to a reader without an account. It is the *shelf* that
   * belongs there and not a header on every pinned feed: a learner's place in a book
   * has nothing to do with which of their saved feeds they are looking at, and a
   * shelf repeated above all of them is a shelf they scroll past.
   *
   * The component answers `null` when there is nothing to continue, which is the
   * reason this is a function rather than a node.
   */
  const renderShelf = useCallback(() => {
    return <FlashBookShelf />
  }, [])

  const homeFeedParams = useMemo<FeedParams>(() => {
    return {
      mergeFeedEnabled: Boolean(preferences.feedViewPrefs.lab_mergeFeedEnabled),
      mergeFeedSources: preferences.feedViewPrefs.lab_mergeFeedEnabled
        ? preferences.savedFeeds
            .filter(f => f.type === 'feed' || f.type === 'list')
            .map(f => f.value)
        : [],
    }
  }, [preferences])

  if (demoMode) {
    return (
      <Pager
        ref={pagerRef}
        testID="homeScreen"
        onPageSelected={onPageSelected}
        renderTabBar={renderTabBar}
        initialPage={selectedIndex}>
        <FeedPage
          testID="demoFeedPage"
          isPageFocused
          isPageAdjacent={false}
          feed="demo"
          renderEmptyState={renderCustomFeedEmptyState}
          feedInfo={pinnedFeedInfos[0]}
        />
        <FeedPage
          testID="customFeedPage"
          isPageFocused
          isPageAdjacent={false}
          feed={`feedgen|${PROD_DEFAULT_FEED('trending')}`}
          renderEmptyState={renderCustomFeedEmptyState}
          feedInfo={pinnedFeedInfos[0]}
        />
      </Pager>
    )
  }

  return hasSession ? (
    <Pager
      key={allFeeds.join(',')}
      ref={pagerRef}
      testID="homeScreen"
      initialPage={selectedIndex}
      onPageSelected={onPageSelected}
      onPageScrollStateChanged={onPageScrollStateChanged}
      renderTabBar={renderTabBar}>
      {homeFeedInfos.length ? (
        homeFeedInfos.map((feedInfo, index) => {
          const feed = feedInfo.feedDescriptor
          const shelf = index === 0 ? renderShelf : undefined
          /*
           * The system For-you page is nobody's saved feed, so it gets no
           * `savedFeedConfig`: there is nothing for it to offer to unpin. It is
           * also the page a reader with nothing pinned lands on, which is where
           * the unpinned-feeds prompt belongs — as that page's empty state.
           */
          const isSystemForYou = !pinnedFeedInfos.includes(feedInfo)
          const emptyState = homeEmptyState({
            isSystemForYou,
            pinnedFeedCount: pinnedFeedInfos.length,
          })
          const renderEmptyState =
            emptyState === 'no-feeds-pinned'
              ? renderNoFeedsPinned
              : emptyState === 'for-you'
                ? renderForYouEmptyState
                : renderCustomFeedEmptyState
          if (feed === 'following') {
            return (
              <FeedPage
                key={feed}
                testID="followingFeedPage"
                isPageFocused={maybeSelectedFeed === feed}
                isPageAdjacent={Math.abs(selectedIndex - index) === 1}
                feed={feed}
                feedParams={homeFeedParams}
                renderEmptyState={renderFollowingEmptyState}
                renderEndOfFeed={FollowingEndOfFeed}
                listHeaderComponent={shelf}
                feedInfo={feedInfo}
              />
            )
          }
          return (
            <FeedPage
              key={feed}
              testID="customFeedPage"
              isPageFocused={maybeSelectedFeed === feed}
              isPageAdjacent={Math.abs(selectedIndex - index) === 1}
              feed={feed}
              renderEmptyState={renderEmptyState}
              savedFeedConfig={isSystemForYou ? undefined : feedInfo.savedFeed}
              listHeaderComponent={shelf}
              feedInfo={feedInfo}
            />
          )
        })
      ) : (
        /*
         * Unreachable while `homeFeedPages` always yields the system page. Kept
         * as the last line of defence: if the list were ever empty, the reader
         * should be offered a way to pin something rather than a blank screen.
         */
        <NoFeedsPinned preferences={preferences} />
      )}
    </Pager>
  ) : (
    /*
     * The front door.
     *
     * Trending, which is the feed the feed generator publishes and the one
     * anybody can read without an account — not the ranked feed: For You is a
     * decision about *this* reader, and a reader the deployment has never seen
     * has no ranking to be shown. It keeps the generic empty state for that same
     * reason; the ranked feed's own copy does not describe it.
     */
    <Pager
      testID="homeScreen"
      onPageSelected={onPageSelected}
      onPageScrollStateChanged={onPageScrollStateChanged}
      renderTabBar={renderTabBar}>
      <FeedPage
        testID="customFeedPage"
        isPageFocused
        isPageAdjacent={false}
        feed={`feedgen|${PROD_DEFAULT_FEED('trending')}`}
        renderEmptyState={renderCustomFeedEmptyState}
        listHeaderComponent={renderShelf}
        feedInfo={pinnedFeedInfos[0]}
      />
    </Pager>
  )
}

const styles = StyleSheet.create({
  loading: {
    height: '100%',
    alignContent: 'center',
    justifyContent: 'center',
    paddingBottom: 100,
  },
})
